/**
 * Оплата (ЮKassa / yoomoney.ru и т.д.).
 *
 * В одной странице часто грузятся и Telegram, и Max SDK. Если сначала вызвать
 * Telegram.WebApp.openLink внутри клиента Max, ссылка может открыться во
 * встроенном фрейме → X-Frame-Options: sameorigin у yoomoney.ru.
 *
 * Порядок: Telegram — переход в том же webview через window.location.href (оплата в текущем окне).
 * Max — WebApp.openLink, иначе window.open. Браузер — window.open / assign.
 */
import { resolveRuntimeMessenger } from "./runtimeMessenger";

/** Trim + parse; только http(s). Иначе iOS Telegram падает: «did not match the expected pattern». */
function normalizePaymentUrl(raw: string): string | null {
  const trimmed = raw.trim();

  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);

    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}

export function openPaymentUrl(url: string): void {
  if (typeof window === "undefined" || !url) return;

  const normalized = normalizePaymentUrl(url);
  if (!normalized) return;

  const { kind } = resolveRuntimeMessenger();

  if (kind === "max") {
    if (typeof window.WebApp?.openLink === "function") {
      try {
        window.WebApp.openLink(normalized)

        return;
      } catch (error: unknown) {
        console.error("Error opening payment url", error);

        window.location.href = normalized;

        return;
      }
    }

    const opened = tryOpenBlankWithoutNavigatingWebview(normalized);

    if (!opened) {
      window.location.href = normalized;
    }

    return;
  }

  if (kind === "telegram") {
    window.location.href = normalized;

    return;
  }

  // Обычный браузер
  try {
    const opened = window.open(normalized, "_blank", "noopener,noreferrer");
    if (!opened || opened.closed) {
      window.location.assign(normalized);
    }
  } catch {
    try {
      window.location.assign(normalized);
    } catch {
      window.open(normalized, "_blank", "noopener,noreferrer");
    }
  }
}

/**
 * Max: не подмешиваем Telegram SDK. Для Max при ошибке openLink — только window.open,
 * без location.assign (часто ломается из‑за X-Frame-Options у страницы оплаты).
 * В Telegram оставляем полный переход через location.href.
 */
function tryOpenBlankWithoutNavigatingWebview(url: string): boolean {
  try {
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (opened && !opened.closed) return true;
    const fallback = window.open(url, "_blank");
    return !!(fallback && !fallback.closed);
  } catch {
    try {
      const opened = window.open(url, "_blank");
      return !!(opened && !opened.closed);
    } catch {
      return false;
    }
  }
}
